import { mergeStyleSets } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPSeparator, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import GenericModal from 'components/Dialogs/GenericModal';
import WarningMessage from 'components/Notification/WarningMessage';
import ScrollableStackItem from 'components/Utils/ScrollableStackItem';
import { globalStackTokensGapSmall, globalTextStylesBold, globalTextStylesError } from 'globalStyles';
import { ApiErrorInfo } from 'models/apiErrorInfo';
import { LogDTO } from 'models/dto/logDTO';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiAddProcessLog } from 'services/Api/userService';
import { apiRequest } from 'services/Auth/authConfig';
import logger, { globalLog } from 'services/Logging/logService';
import AppError from 'utils/appError';
import { safeStringify } from 'utils/stringify';

interface IDialogNotificationList {
  isOpen: boolean;
  items: ApiErrorInfo[];
  title: string;
  subTitle: string;
  createTicket?: boolean;
  onClose: () => void;
}

export const DialogErrorList = (props: IDialogNotificationList) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation']);
  const [ticketCreated, setTicketCreated] = useState<boolean>(false);

  useEffect(() => {
    const onCreateTicket = async (errCount: number) => {
      try {
        const logDTO = new LogDTO(`Global client log for (${errCount}) bulk errors`);
        globalLog.messages.forEach((m) => logDTO.add(m));
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        await apiAddProcessLog(logDTO, accessToken);
        globalLog.clear();
        setTicketCreated(true);
      } catch (err) {
        logger.error(new AppError('Error while uploading log to API. ' + safeStringify(err)));
        appContext.showNotification(t('translation:Errors.UploadClientLog.Message'), true);
      }
    };

    if (props.isOpen && props.createTicket) {
      let createTicket: number = 0;

      //when the errors contain a 'non' controller exception, log these error into the global error logger
      for (const item of props.items) {
        if (!item.controllerExceptionCode) {
          logger.error(new AppError(`Task bulk update: ${item.displayMessage}`));
          logger.debug('Error details:', item);
          createTicket++;
        }
      }

      if (createTicket > 0) {
        onCreateTicket(createTicket);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen, props.items]);

  const getItemStyles = (item: ApiErrorInfo, isBold: boolean) => {
    let style;

    if (isBold) {
      style = !item.controllerExceptionCode
        ? mergeStyleSets(globalTextStylesBold, globalTextStylesError)
        : globalTextStylesBold;
    } else {
      style = !item.controllerExceptionCode ? globalTextStylesError : undefined;
    }

    return style;
  };

  //
  // Main render
  //
  return (
    <GenericModal
      isOpen={props.isOpen}
      onClose={props.onClose}
      title={props.title}
      subTitle={props.subTitle}
      height={500}
      width={700}
      maxWidth={'min(90vw, 700px)'}
      maxHeight={'90vh'}
      minHeight={300}
      minWidth={300}
    >
      <IPStack verticalFill tokens={globalStackTokensGapSmall}>
        <IPStack.Item>
          <IPSeparator />
        </IPStack.Item>
        {ticketCreated && (
          <IPStack.Item>
            <WarningMessage message={t('translation:Errors.UploadClientLog.TicketHasBeenCreated')} />
          </IPStack.Item>
        )}
        <ScrollableStackItem>
          {props.items.map((item, index) => (
            <IPStack horizontal tokens={globalStackTokensGapSmall} key={'item-' + index}>
              {item.entity && (
                <IPStack.Item styles={{ root: { width: '30%', paddingBottom: 10 } }}>
                  <IPText
                    styles={getItemStyles(item, true)}
                  >{`${item.entity.entityId} - ${item.entity.entityName}`}</IPText>
                </IPStack.Item>
              )}
              <IPStack.Item styles={{ root: { width: '70%', paddingBottom: 10 } }}>
                <IPText variant="small" styles={getItemStyles(item, false)}>
                  {item.displayMessage}
                </IPText>
              </IPStack.Item>
            </IPStack>
          ))}
        </ScrollableStackItem>
      </IPStack>
    </GenericModal>
  );
};
